"use client";

import { Fragment, useEffect, useState } from "react";
import { FeedEvents, trackEvent } from "@/analytics";

import type { FeedCampaign } from "./campaigns";
import Image from "next/image";
import { PunchupCampaignBanner } from "@punchup/ui-common/punchup/composites/marketing/PunchupCampaignBanner";
import { rememberCampaignDismissal } from "./dismissal";

interface Props {
  campaign: FeedCampaign;
}

/**
 * The home feed's partner-campaign slot, shown to signed-in and signed-out
 * visitors alike. Gating happens upstream in the feed page — if this renders at
 * all, the campaign is live.
 */
export default function FeedCampaignBanner({ campaign }: Props) {
  // Hides it for the rest of this page view; the cookie keeps it gone on the
  // next one, where the server drops it before render.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    trackEvent(FeedEvents.VIEWED_FEED_CAMPAIGN_BANNER, {
      campaignId: campaign.id,
    });
  }, [campaign.id]);

  const handleDismiss = () => {
    rememberCampaignDismissal(campaign.id);
    trackEvent(FeedEvents.DISMISSED_FEED_CAMPAIGN_BANNER, {
      campaignId: campaign.id,
    });
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div className="px-4 md:px-0 pb-4 mb-4 border-b md:pb-6 md:mb-6 border-neutral-200">
      <PunchupCampaignBanner
        artwork={
          <Image
            src={campaign.artwork.src}
            alt={campaign.artwork.alt}
            width={campaign.artwork.width}
            height={campaign.artwork.height}
            priority
            className="h-auto w-full max-w-[261px] md:max-w-none"
          />
        }
        headline={campaign.headlineLines.map((line, index) => (
          <Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </Fragment>
        ))}
        lineup={campaign.lineup}
        lineupTrailing={campaign.lineupTrailing}
        ctaLabel={campaign.ctaLabel}
        ctaHref={campaign.ctaHref}
        ctaExternal={campaign.ctaExternal}
        onCtaClick={() =>
          trackEvent(FeedEvents.CLICKED_FEED_CAMPAIGN_BANNER, {
            campaignId: campaign.id,
            url: campaign.ctaHref,
            cta: "primary",
          })
        }
        secondaryCtaLabel={campaign.secondaryCtaLabel}
        secondaryCtaHref={campaign.secondaryCtaHref}
        onSecondaryCtaClick={() =>
          trackEvent(FeedEvents.CLICKED_FEED_CAMPAIGN_BANNER, {
            campaignId: campaign.id,
            url: campaign.secondaryCtaHref,
            cta: "secondary",
          })
        }
        attribution={
          <>
            Ticketing powered by
            {/* Brand logo (mark + wordmark, orange) — the header's asset, not
             * the plain white wordmark. */}
            <Image
              src="/resources/punchupLogoIcon.svg"
              alt="Punchup"
              width={100}
              height={17}
            />
          </>
        }
        onDismiss={handleDismiss}
        dismissLabel={`Dismiss ${campaign.artwork.alt} banner`}
        theme={campaign.theme}
      />
    </div>
  );
}
